import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { API_URL_V4, environment } from 'src/environments/environment.local';
import * as formatDataHelpers from '../helpers/format-browse-data.helpers';
import * as filterHelpers from '../helpers/filter.helpers';
import * as sortingHelpers from '../helpers/sorting.helpers';
import { BrowseFilter } from '../models/filter';
import { BrowseSorting } from '../models/sorting';
import { Sport } from '../models/sport';
import { ApiHttpResponse, Coaching } from '@kinomap-dev/ng-ui-lib';
import { CoachingInterval } from '@kinomap-dev/ng-ui-lib/lib/models/coaching-interval.model';
import { Urls } from '@kinomap-dev/ng-ui-lib/lib/models/urls.model';

@Injectable({ providedIn: 'root' })
export class CoachingApi {
  private readonly coachingUrl = `${API_URL_V4}/coaching`;
  private readonly limit: number = 15;
  private lastSelectedSport: Sport | null = null;
  private lastFilters: BrowseFilter[] = [];
  private lastSorting: BrowseSorting | null = null;
  private params: HttpParams = new HttpParams();
  isLastPageLoaded: boolean = false;

  constructor(private http: HttpClient) {
    this.params = this.params.set('limit', this.limit).set('page', 1);
  }

  getCoachings$(
    selectedSport: Sport = this.lastSelectedSport,
    filters: BrowseFilter[] = this.lastFilters,
    sorting: BrowseSorting = this.lastSorting,
    nextPage: boolean
  ): Observable<Coaching[]> {
    this.params = this.params
      .set('sport', selectedSport.name)
      .set('sortDirection', filterHelpers.getSortDirectionParam(filters))
      .set('limit', 15)
      .set('sortType', sortingHelpers.getSortTypeParam(sorting));
    this.setFilterParams(filters);

    if (!nextPage) {
      this.params = this.params.set('page', 1);
      this.isLastPageLoaded = false;
    }

    return this.http
      .get<
        ApiHttpResponse<Coaching[]>
      >(this.coachingUrl, { params: this.params })
      .pipe(
        map((res: ApiHttpResponse<Coaching[]>) => {
          const currentPage: number = +res.meta.current_page;
          const lastPage: number = +res.meta.last_page;

          if (currentPage === lastPage) {
            this.isLastPageLoaded = true;
          } else {
            this.params = this.params.set('page', currentPage + 1);
          }

          this.lastSelectedSport = { ...selectedSport };
          this.lastFilters = [
            ...filters.map((f: BrowseFilter) => {
              return { ...f };
            }),
          ];
          this.lastSorting = { ...sorting };
          return res.data;
        })
      );
  }

  getCoaching$(coachingId: number): Observable<Coaching> {
    const url: string = environment.api.coaching.coachingDetails.replace(
      '{coachingId}',
      coachingId.toString()
    );
    return this.http.get<ApiHttpResponse<Coaching>>(url).pipe(
      map((item: ApiHttpResponse<Coaching>) => {
        return item.data;
      })
    );
  }

  getVideoUrls$(coachingId: number): Observable<Urls> {
    const url: string = environment.api.coaching.coachingUrls.replace(
      '{coachingId}',
      coachingId.toString()
    );
    return this.http.get(url).pipe(
      map((item: any) => {
        return formatDataHelpers.convertApiDataToCoachingUrls(item.data);
      })
    );
  }

  getIntervals$(coachingId: number): Observable<CoachingInterval[]> {
    const url: string = environment.api.coaching.coachingIntervals.replace(
      '{coachingId}',
      coachingId.toString()
    );
    return this.http.get(url).pipe(
      map((res: any) => {
        return res.data.map((item: any) => {
          return formatDataHelpers.convertApiDataToInterval(item);
        });
      })
    );
  }

  /**
   *  Post coaching new intervals.
   * @param coachingId:number
   * @param intervals:CoachingInterval[]
   * @returns
   */
  setIntervals(coachingId: number, intervals: CoachingInterval[]) {
    const url: string = environment.api.coaching.setCoachingIntervals.replace(
      '{coachingId}',
      coachingId.toString()
    );
    return this.http.post<CoachingInterval[]>(url, intervals);
  }

  getCoachingByCoach(
    coachId: number,
    limit: number = 15,
    nextPage: boolean
  ): Observable<Coaching[]> {
    // URL
    const url: string = environment.api.coaching.videosByCoach.replace(
      '{coachId}',
      coachId.toString()
    );

    // PARAMETERS
    this.params = this.params.set('limit', limit);

    if (!nextPage) {
      this.params = this.params.set('page', 1);
      this.isLastPageLoaded = false;
    }

    return this.http.get(url, { params: this.params }).pipe(
      map((res: any) => {
        const currentPage: number = +res.meta.current_page;
        const lastPage: number = +res.meta.last_page;

        if (currentPage === lastPage) {
          this.isLastPageLoaded = true;
        } else {
          this.params = this.params.set('page', currentPage + 1);
        }

        return res.data;
      })
    );
  }

  getUsersLiked(coachingId: number, page: number): Observable<any> {
    const url: string = environment.api.coaching.like.replace(
      '{coachingId}',
      coachingId.toString()
    );
    const param: HttpParams = new HttpParams().set('page', page);
    return this.http.get(url, { params: param }).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  postLike(coachingId: number): Observable<any> {
    const url: string = environment.api.coaching.like.replace(
      '{coachingId}',
      coachingId.toString()
    );
    return this.http.post(url, {}).pipe(map((res: any) => res.data));
  }

  deleteLike(coachingId: number): Observable<any> {
    const url: string = environment.api.coaching.like.replace(
      '{coachingId}',
      coachingId.toString()
    );
    return this.http.delete(url, {}).pipe(map((res: any) => res.data));
  }

  private setFilterParams(filters: BrowseFilter[]): void {
    this.setDurationParams(filters);
    this.setDifficultiesParam(filters);
    this.setSoundParams(filters);
    this.setLangGroupParam(filters);
    this.setMachineTypeParam(filters);
  }

  private setDurationParams(filters: BrowseFilter[]): void {
    const [min, max] = filterHelpers.getDurationParams(filters);
    this.params = this.params.set('minDuration', min).set('maxDuration', max);
  }

  private setDifficultiesParam(filters: BrowseFilter[]): void {
    const diffParam: string[] = filterHelpers.getDifficultiesParam(filters);
    if (this.params.has('difficulties[]')) {
      this.params = this.params.delete('difficulties[]');
    }
    diffParam.forEach((difficulty: string) => {
      this.params = this.params.append('difficulties[]', difficulty);
    });
  }

  private setSoundParams(filters: BrowseFilter[]): void {
    const soundParam: string = filterHelpers.getSoundParam(filters);
    this.params = this.params.set('soundAvailable', soundParam);
  }

  private setLangGroupParam(filters: BrowseFilter[]): void {
    const languages: string[] = filterHelpers.getLangGroupParam(filters);
    languages.forEach(
      (language: string) =>
        (this.params = this.params.set('langGroup[]', language))
    );
  }

  private setMachineTypeParam(filters: BrowseFilter[]): void {
    const machineTypes: string[] = filterHelpers.getMachineTypeParam(filters);
    if (this.params.has('machineTypes[]')) {
      this.params = this.params.delete('machineTypes[]');
    }
    machineTypes.forEach(
      (machineType: string) =>
        (this.params = this.params.append('machineTypes[]', machineType))
    );
  }
}
